import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { FormArray } from '@angular/forms';
import moment from 'moment';
import { NgIf, NgFor, NgClass, DecimalPipe, TitleCasePipe, KeyValuePipe } from '@angular/common';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { TranslateModule } from '@ngx-translate/core';

import { CodeValue } from '../../../../shared/models/boc-available-value';
import { WEEKYEAR_FORMAT_PATTERN } from '../../../../shared/models/filter-formats';
import { UserService } from '../../../../shared/services/user.service';
import { UNDERSCORE } from '../../../../shared/utils/constant';
import { EorderProduct, RDD_TYPE_DAY, RDD_TYPE_WEEK, FORM_SHEET_CODE,
	ProductFamily, VariableField } from '../../../models/eorder';
import { EorderWizardStep } from '../../../models/eorder-wizard-state';
import { FormEorder, FormProduct } from '../../../models/form-eorder';
import { CharacteristicValueSimpleObject } from '../../../../shared/models/product';
import { EorderWizardStepperService } from '../../../services/eorder-wizard-stepper.service';
import { EorderWizardService } from '../../../services/eorder-wizard.service';
import { EorderApiService } from '../../../services/eorder.api.service';
import { WizardPageContent } from '../wizard-page-content';
import { BusinessType } from '../../../../shared/models/business-type';
import { DatePipe } from '../../../../shared/pipes/date.pipe';
import {
	NAMESPACE_RTPC_FIELD,
	ProductCharacteristicsTranslationService
} from '../../../../shared/services/product-characteristics-translation.services';
import { CodeValue as CodeValueType } from '../../../../shared/models/code-value';

@Component({
	selector: 'copo-eorder-step-summary',
	templateUrl: './step-summary.component.html',
	styleUrl: './step-summary.component.css',
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		TranslateModule,
		MatExpansionModule,
		MatIconModule,
		MatButtonModule,
		NgIf,
		NgFor,
		NgClass,
		DecimalPipe,
		DatePipe,
		TitleCasePipe,
		KeyValuePipe
	]
})

export class StepSummaryComponent extends WizardPageContent implements OnInit {
	readonly form: FormEorder;
	readonly UNDERSCORE = UNDERSCORE;
	productsList?: EorderProduct[] = [];
	soldto?: string;
	isSheet = false;
	tmodes: CodeValue[] = [];
	currencyList?: CodeValue[];
	isExpanded: boolean[] = [];
	businessTypes: BusinessType[] = [];
	serviceLevels: CodeValue[] = [];
	xcarbParamList: CodeValue[] = [];
	productFamily?: ProductFamily;
	rddTypeWeek = RDD_TYPE_WEEK;
	rddTypeDay = RDD_TYPE_DAY;
	private readonly VIEW_STEP_NUM = 2;

	constructor(
		private readonly eorderWizardService: EorderWizardService,
		private readonly eorderStepperService: EorderWizardStepperService,
		private readonly eorderApiService: EorderApiService,
		private readonly userService: UserService,
		private readonly productCharacteristicsTranslationService: ProductCharacteristicsTranslationService
	) {
		super(EorderWizardStep.SUMMARY, eorderWizardService.getState());
		this.form = this.eorderWizardService.getStateEorderForm();
		this.productsList = <EorderProduct[]> this.eorderWizardService.getStateEorderForm().getFormProducts().value;
		this.productFamily = this.eorderWizardService.getState().ui.getSelectedProductFamily();
		for (let i = 0; i < this.productsList.length; i++) {
			this.isExpanded[i] = true;
		}
		this.tmodes = this.userService.getBocAvailableValuesBasedOnCode('EORDR', 'TMODE') || [];
		if (
			this.userService.user.hasServiceParameterEqualsTo('EORDR', 'BusinessType', 'Open')
			&& this.businessTypes.length === 0
		) {
			this.userService.getBusinessTypes(<string> this.form.getFormSoldTo().value).then(businessTypes => {
				this.businessTypes = businessTypes;
				this.businessTypes.unshift({
					businessCode: '',
					businessDescription: 'None'
				});
			}).catch((error: unknown) => {
				console.error('Error fetching business types:', error);
			});
		}
		this.serviceLevels = this.userService.getBocAvailableValuesBasedOnCode('EORDR', 'SERVICELEVEL') || [];
		this.xcarbParamList = this.userService.getBocAvailableValuesBasedOnCode('EORDR', 'XCARB') || [];
		if (this.xcarbParamList.length > 0 && !this.xcarbParamList.some(param => param.code === '')) {
			this.xcarbParamList.unshift({
				code: '',
				description: 'None',
				isDefault: true
			});
		}
		this.eorderWizardService.createRequestOrderObj();
		this.currencyList = this.bocAvailableValues('CURRENCY');
	}

	ngOnInit(): void {
		const soldtoShiptoList = this.eorderApiService.filteredSoldtoShiptoList;
		const selSoldtoId = <string> this.form.getFormSoldTo().value;
		if (soldtoShiptoList) {
			const selectedSoldtoList = soldtoShiptoList.filter(v => v.businessId === selSoldtoId);
			for (const soldtoList of selectedSoldtoList) {
				this.soldto = `${ soldtoList.name } - ${ soldtoList.city }`;
			}
		}
		if (this.form.getFormValue() === FORM_SHEET_CODE) {
			this.isSheet = true;
		}
		this.eorderStepperService.onStepChanged.emit({ step: this.VIEW_STEP_NUM });
	}

	checkError(): boolean {
		return false;
	}

	checkErrorForDraft() {
		return false;
	}

	expandPanel(index: number) {
		this.isExpanded[index] = !this.isExpanded[index];
	}

	getExpandPanelStatus(index: number) {
		return this.isExpanded[index];
	}

	getRddForWeek(rdd: string) {
		return moment(rdd).format(WEEKYEAR_FORMAT_PATTERN);
	}

	getVariableFieldLabel(fieldLabel: string) {
		const field = fieldLabel.replace(/([A-Z])/g, ' $1');
		return field.charAt(0).toUpperCase() + field.slice(1).toLowerCase();
	}

	getSelectedShipTo(shipToCode?: string) {
		const soldtoShiptoList = this.eorderApiService.filteredSoldtoShiptoList;
		if (soldtoShiptoList && shipToCode) {
			const selectedShipto = soldtoShiptoList.find(v => v.businessId === shipToCode);
			if (selectedShipto) {
				return `${ selectedShipto.name } - ${ selectedShipto.city }`;
			}
		}
		return '';
	}

	getTmodeValue(tmodeCode?: string) {
		if (this.tmodes.length > 0 && tmodeCode) {
			const selectedTmode = this.tmodes.find(field => field.code === tmodeCode);
			if (selectedTmode) {
				return selectedTmode.description;
			}
		}
		return '';
	}

	getServiceLevelValue(serviceCode?: string) {
		if (this.serviceLevels.length > 0 && serviceCode) {
			const selectedServiceLevel = this.serviceLevels.find(field => field.code === serviceCode);
			if (selectedServiceLevel) {
				return selectedServiceLevel.description;
			}
		}
		return '';
	}

	getXCarbValue(xcarbCode?: string) {
		if (this.xcarbParamList.length > 0) {
			const selectedXCarbParam = this.xcarbParamList.find(field => field.code === xcarbCode);
			if (selectedXCarbParam) {
				return selectedXCarbParam.description;
			}
		}
		return '';
	}

	getBusinessTypeValue(businessCode?: string) {
		if (this.businessTypes.length > 0) {
			const selectedBusinessType = this.businessTypes.find(field => field.businessCode === businessCode);
			if (selectedBusinessType) {
				return selectedBusinessType.businessDescription;
			}
		}
		return '';
	}

	getPossibleValues(formIndex: number, variableName: string): CodeValueType[] {
		const templateNumber = this.getTemplateNumber(formIndex);
		if (templateNumber) {
			const valuesPossiblePerField = this.eorderWizardService.getValuesPossiblePerField(templateNumber);
			return valuesPossiblePerField.filter(v => v.keys === variableName).flatMap(v => v.values);
		}
		return [];
	}

	getVariabletranslation(formIndex: number, variableName: string, code?: string) {
		const templateNumber = this.getTemplateNumber(formIndex);
		if (code) {
			const valuesPossiblePerField = this.eorderWizardService.getValuesPossiblePerField(templateNumber);
			const variableFields = valuesPossiblePerField.filter(v => v.keys === variableName).flatMap(v => v.values);
			const variableField = variableFields.find(v => v.code === code);
			if (variableField) {
				return variableField.value;
			}
			return this.checkConfigDataUsed(formIndex, variableName, code);
		}
		return '';
	}

	getTemplateNumber(formIndex: number) {
		const formProduct = this.getFormProduct(formIndex);
		return formProduct.getProduct().templateNumber || 0;
	}

	getVariableFormGroup(formIndex: number) {
		const formProduct = this.getFormProduct(formIndex);
		return formProduct.getVariableFormGroup();
	}

	getVariableFieldDescription(formIndex: number, key: string, freeType?: boolean, codeValues?: CodeValueType[]) {
		const formProduct = this.getFormProduct(formIndex);
		const variableFormGroup = formProduct.getVariableFormGroup();
		const variableControl = variableFormGroup.get(key);
		if (variableControl?.value) {
			if (freeType) {
				return String(variableControl.value);
			}
			if (codeValues) {
				for (const codeValue of codeValues) {
					if (codeValue.code === variableControl.value) {
						return codeValue.value;
					}
				}
			}
			return this.checkConfigDataUsed(formIndex, key, String(variableControl.value));
		}
		return '';
	}

	getFieldLabel(fieldLabel: string) {
		return this.productCharacteristicsTranslationService
			.translate(`${ NAMESPACE_RTPC_FIELD }${ fieldLabel }`);
	}

	checkConfigDataUsed(index: number, key: string, value: string) {
		const formProduct = this.getFormProduct(index);
		const configurationControl = formProduct.getConfigurationNumber();
		const configurationNumber = String(configurationControl.value);
		if (configurationNumber && this.eorderWizardService.getState().ui.hasConfigurationData(configurationNumber)) {
			const configData = this.eorderWizardService.getState().ui.getConfigurationData(configurationNumber);
			// eslint-disable-next-line @typescript-eslint/no-base-to-string
			if (configData?.[key] && this.removeTranslationPrefix(String(configData[key])) === value) {
				// eslint-disable-next-line @typescript-eslint/no-base-to-string
				return this.productCharacteristicsTranslationService.translate(String(configData[key]));
			}
		}
		return value;
	}

	getVariableFieldDescriptionMultiItems(
		contentChildFieldName: string,
		formIndex: number,
		index: number,
		contentParentFieldName: string,
		codeValues?: CodeValueType[],
		freeType?: boolean
	) {
		const formProduct = this.getFormProduct(formIndex);
		const variableFormGroup = formProduct.getVariableFormGroup();
		const variableControl = <FormArray> variableFormGroup.get(contentParentFieldName);
		if (variableControl.controls[index]?.value) {
			const value = String(variableControl.controls[index]?.value);
			if (freeType) {
				return value;
			}
			if (codeValues) {
				for (const codeValue of codeValues) {
					if (codeValue.code === value) {
						return codeValue.value;
					}
				}
			}
			return this.checkConfigUsedMultiItems(
				contentChildFieldName,
				formIndex,
				index,
				contentParentFieldName,
				value
			);
		}
		return '';
	}

	checkConfigUsedMultiItems(
		contentChildFieldName: string,
		formIndex: number,
		index: number,
		contentParentFieldName: string,
		value: string
	) {
		const configData = this.getConfigDataMultiItems(formIndex, contentParentFieldName);
		if (configData) {
			const arrayObject = <CharacteristicValueSimpleObject[]>configData;
			const configValue = arrayObject[index] ? arrayObject[index][contentChildFieldName] : undefined;
			if (configValue && this.removeTranslationPrefix(String(configValue)) === value) {
				return this.productCharacteristicsTranslationService.translate(String(configValue));
			}
		}
		return value;
	}

	getConfigDataMultiItems(formIndex: number, contentParentFieldName: string) {
		const formProduct = this.getFormProduct(formIndex);
		const configurationControl = formProduct.getConfigurationNumber();
		const configurationNumber = String(configurationControl.value);
		if (configurationNumber && this.eorderWizardService.getState().ui.hasConfigurationData(configurationNumber)) {
			const configData = this.eorderWizardService.getState().ui.getConfigurationData(configurationNumber);
			if (configData?.[contentParentFieldName]) {
				return configData[contentParentFieldName];
			}
		}
		return undefined;
	}

	getVariablesForThirdRow(index: number) {
		const templateNumber = this.getTemplateNumber(index);
		if (templateNumber) {
			return this.eorderWizardService.getVariablesForThirdRow(templateNumber);
		}
		return [];
	}

	getOptionalToleranceFields(index: number) {
		const templateNumber = this.getTemplateNumber(index);
		if (templateNumber) {
			return this.eorderWizardService.getOptionalToleranceFields(templateNumber);
		}
		return new Map<string, VariableField[]>();
	}

	getVariableFormArray(index: number, field: string) {
		const formProduct = this.getFormProduct(index);
		const variableFormGroup = formProduct.getVariableFormGroup();
		return (<FormArray> variableFormGroup.get(field)).controls;
	}

	getVariableFormArrayValueByIndex(formIndex: number, index: number, field: string) {
		const formProduct = this.getFormProduct(formIndex);
		const variableFormGroup = formProduct.getVariableFormGroup();
		const variableFieldArray = <FormArray> variableFormGroup.get(field);
		return <string> variableFieldArray.controls[index].value;
	}

	getCurrencyDisplay(code: string): string {
		if (this.currencyList && this.currencyList.length > 0) {
			const currency = this.currencyList.find(c => c.code === code);
			return currency ? `${ currency.code } - ${ currency.description }` : code;
		}
		return code;
	}

	private getFormProduct(index: number): FormProduct {
		const products = <FormArray> this.form.get('products');
		return <FormProduct> products.controls[index];
	}

	private removeTranslationPrefix(value: string): string {
		return value.slice(Math.max(0, value.lastIndexOf('.') + 1));
	}

	private bocAvailableValues(parameter: string) {
		return this.userService.getBocAvailableValuesBasedOnCode('EORDR', parameter);
	}
}
